import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { ExternalSearchSystemResponse } from "@/Common/Api/PatientExternalSearch/Dto/ExternalSearchSystemResponse";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { ComboBoxControl } from "@/Common/Components/Inputs/ComboboxControl";
import { Popup } from "@/Common/Components/Modals/Popup";
import { HealthCardHelper } from "@/Common/Helpers/HealthCareHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { Gender } from "@/Common/Models/Patient/PatientEnums";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { PatientAddress, PatientPhoneNumber, SearchPatientV2Result } from "@/Patient/Api/Dto/Responses/SearchPatientV2Result";
import { ExternalSearchData, ExternalSearchResponse, ExternalSystemIds } from "@/Patient/Models/ExternalSearchData";

import "./ExternalSearchComponent.scss";

import img_loading from "@/Globals/_images/loading.gif";
import img_p_banner_icon_100 from "@/Globals/_images/p-banner-icon-100.png";
import img_p_banner_icon_100_transparent from "@/Globals/_images/p-banner-icon-100-transparent.png";

interface IExternalSearchComponent {
    DefaultExternalSearchSystemId?: string;
    ExternalSearchSystems?: ExternalSearchSystemResponse[];
    patientIdentifier?: string;
    onCancelCallback: () => void;
    onSyncCallBack?: () => void;
    patientToUpdateContactid?: string;
}

class ExternalSearchComponentState {
    public showNamWarningPopup: boolean;
    public isLoading: boolean;
    public errorMessage: string;

    // external search
    public externalQueryText: string;
    public pendingExternalSearch: boolean; // text debouncing
    public isSearchingExternal: boolean; // request pending
    public externalSearchPatients: { [refKey: string]: ExternalSearchData };
    public selectedExternalPatientRefkey: string;
    public selectedExternalSearchSystemCode: string;

    // myle search
    public myleQueryText: string;
    public mylePatients: SearchPatientV2Result[];

    public static getDefault(props: IExternalSearchComponent): ExternalSearchComponentState {
        const state = new ExternalSearchComponentState();
        state.selectedExternalSearchSystemCode = props.ExternalSearchSystems?.firstPreferredOrDefault(p => p.Id == props.DefaultExternalSearchSystemId)?.Code;
        state.externalQueryText = props.patientIdentifier ?? "";
        state.externalSearchPatients = {};

        state.myleQueryText = "";
        state.mylePatients = [];

        state.showNamWarningPopup = false;
        state.isLoading = false;
        state.errorMessage = "";

        return state;
    }
}

class ExternalSearchComponent extends BaseReactComponent<IExternalSearchComponent, ExternalSearchComponentState> {
    private debounceExternalSearchRef: number = -1;
    private id = "ExternalSearchComponent";

    public constructor(props: IExternalSearchComponent) {
        super(props);

        this.state = ExternalSearchComponentState.getDefault(this.props);
    }

    public componentDidMount(): void {
        this.setOverlay("externalSearch");

        if (!IsNullOrWhiteSpace(this.state.externalQueryText))
            this.searchExternalPatient();
        else
            this.setFocusOnInput();

        let elem = $(`#${this.id}`);
        let cnt = document.body;
        let dragOptions = {
            containment: cnt,
            handle: ".es-header",
            stop: () => {
                elem.draggable("destroy");
                window.setTimeout(() => {
                    elem.draggable(dragOptions);
                }, 80);
            }
        };

        elem.draggable(dragOptions);
    }

    public setFocusOnInput(): void {
        window.setTimeout(() => {
            $("#SearchInputId").focus();
            $("#SearchInputId").select();
        }, 200);
    }

    public render(): JSX.Element {
        return (
            <>

                <div className="external-search" id={this.id}>
                    {this.state.showNamWarningPopup && this.renderNAMInvalidWarningPopup()}
                    {/* HEADER */}
                    <div className="es-header draggable">
                        <h2 className="thin-font">{cacheStore.GetString("ExternalPatientSearchLabel")}</h2>
                        <a className="close" onClick={() => this.props.onCancelCallback()}>x</a>
                    </div>

                    {/* BODY */}
                    <div className="es-main">
                        {/* LEFT | Clinibase DATA */}
                        {this.renderExternalPatientSearch()}
                        {/* RIGHT | MYLE DATA */}
                        {this.mylePatientSearch()}
                    </div>

                    {/* FOOTER */}
                    <div className="es-footer">
                        {this.renderFooter()}
                    </div>
                </div>
            </>
        );
    }

    private renderNAMInvalidWarningPopup(): JSX.Element {
        return (
            <Popup
                title={cacheStore.GetString("WarningLabel")}
                draggable={false}
                manageOverlay
                standardFooterProps={{
                    okButton: {
                        onClick: () => {
                            this.mutateState(s => s.showNamWarningPopup = false);
                            this.importExternalPatient(false);
                        }
                    },
                    cancelButton: {
                        onClick: () => this.mutateState(s => s.showNamWarningPopup = false)
                    } }
                }
            >
                <div className="p-body">{cacheStore.GetString("ExternalPatientExport_InvalidNAM_Warning")}</div>
            </Popup>
        );
    }

    private validateNAMPatient(selectedPatient: ExternalSearchData): boolean {
        return HealthCardHelper.isHealthCardValid(
            selectedPatient.NAM,
            cacheStore.FindLookupByLookupCode("CanadaState_QC").LookupID,
            cacheStore.FindLookupByLookupCode("CNCANADA").LookupID,
            selectedPatient.FirstName,
            selectedPatient.LastName,
            new Date(selectedPatient.BirthDate),
            cacheStore.GetGenderLookupId(selectedPatient.Sex)
        );
    }

    private onImportClick(): void {
        const selectedPatient = this.state.externalSearchPatients?.[this.state.selectedExternalPatientRefkey] ?? null;
        if (!selectedPatient)
            return;

        if (!this.validateNAMPatient(selectedPatient)) {
            this.mutateState(s => s.showNamWarningPopup = true);
        }
        else {
            this.importExternalPatient(true);
        }
    }

    private renderFooter(): JSX.Element {
        const updatedDisabled = !this.state.selectedExternalPatientRefkey || this.state.mylePatients.length != 1 || this.state.isLoading;
        const createDisabled = !this.state.selectedExternalPatientRefkey || this.state.mylePatients.length != 0 || this.state.isLoading;
        const showUpdateButton = !updatedDisabled;
        const showCreateButton = updatedDisabled;

        return (
            <>
                <div>
                    {(this.state.pendingExternalSearch == true || this.state.isSearchingExternal == true) && (
                        <div style={{ marginRight: "5px" }}>
                            <img src={img_loading} alt="loading" />
                        </div>
                    )}
                    {this.state.isSearchingExternal == true && (
                        <div style={{ marginTop: "15px" }}>
                            <strong>...</strong>
                        </div>
                    )}
                    {this.state.errorMessage && (
                        <div style={{ marginTop: "5px", marginRight: "5px", overflow: "hidden", maxHeight: "15px" }} title={this.state.errorMessage}>
                            <span style={{ color: "#ff0000" }}>{this.state.errorMessage}</span>
                        </div>
                    )}
                </div>
                <div>
                    <button
                        className="btn btn-default"
                        style={{ marginRight: "5px" }}
                        onClick={() => this.props.onCancelCallback()}
                    >
                        {cacheStore.GetString("CancelLabel")}
                    </button>
                    {showUpdateButton && (
                        <button
                            disabled={updatedDisabled}
                            className="btn btn-primary"
                            style={{ marginRight: "5px" }}
                            onClick={() => this.updatePatient(this.state.mylePatients[0])}
                        >
                            {cacheStore.GetString("UpdateLabel")}
                        </button>
                    )}
                    {showCreateButton && (
                        <button
                            disabled={createDisabled}
                            className="btn btn-primary"
                            onClick={() => this.onImportClick()}
                        >
                            {cacheStore.GetString("ImportLabel")}
                        </button>
                    )}
                </div>
            </>
        );
    }

    private renderExternalPatientSearch(): JSX.Element {
        return (
            <div className="external-view">
                <div className="external-view-search">
                    <div className="search-component flex-container form-control" style={{ width: "auto", flexGrow: "1" }}>
                        <span className="glyphicon glyphicon-search" />
                        <input
                            id="SearchInputId"
                            type="text"
                            value={this.state.externalQueryText}
                            placeholder={cacheStore.GetString("SearchLabel")}
                            onChange={(e: any) => this.onExternalSearchTextChange(e.target.value)}
                            onKeyDown={e => this.onQueryKeyDown(e)}
                        />
                    </div>
                    {this.props.ExternalSearchSystems?.length > 1 && (
                        <ComboBoxControl
                            id="_DSQLocationTypeComboBox"
                            className="external-view-search-location"
                            items={this.props.ExternalSearchSystems.map(p => new ComboBoxDropDownItem(p.Code, p.Name)).toImmutable()}
                            selectedCode={this.state.selectedExternalSearchSystemCode}
                            style={{ width: "175px", marginLeft: "5px" }}
                            onChange={value => {
                                this.mutateState(s => s.selectedExternalSearchSystemCode = value, () => this.debouncedExternalSearchTextChange());
                            }}
                        />
                    )}
                </div>
                <div>
                    {this.renderExternalPatientSearchContent()}
                </div>

            </div>
        );
    }

    private renderExternalPatientSearchContent(): JSX.Element {
        const patients = this.state.externalSearchPatients;
        if (!patients)
            return null;

        return (
            <div className="flex-container flex-column">
                {patients && Object.keys(patients)
                    .map(p => patients[p])
                    .map(patient => {
                        const isCurrentlySelected = this.state.selectedExternalPatientRefkey == patient.MRN;
                        const selectedClassName = isCurrentlySelected ? "selected" : "";

                        return (
                            <div
                                key={`PatientResponse_${patient.MRN}`}
                                className={`myle-m-patient pointer ${selectedClassName}`}
                                style={{ padding: "unset" }}
                                onClick={() => this.onSelectExternalPatient(patient.MRN)}
                            >
                                <div className="eternal-search-patient-card">
                                    <div className="logo">
                                        <img src={img_p_banner_icon_100_transparent} />
                                    </div>
                                    <div>
                                        <div>
                                            <strong>{`${patient.FirstName} ${patient.LastName} [${patient.Sex}]`}</strong>
                                        </div>
                                        <div>
                                            <strong>NAM: </strong>
                                            <span>{`${patient.NAM ?? ""} ${this.getFormattedDateString(patient.NAMExp, "(YYYY-MM)")}`}</span>
                                        </div>
                                        <div>
                                            <strong>{cacheStore.GetString("DateOfBirthDLabel")} </strong>
                                            <span>{this.getFormattedDateString(patient.BirthDate, "YYYY-MM-DD")}</span>
                                        </div>
                                        <div>
                                            <strong>{cacheStore.GetString("lblMother")}: </strong>
                                            <span>{`${patient.MotherFirstName ?? ""} ${patient.MotherLastName ?? ""}`}</span>
                                        </div>
                                        <div>
                                            <strong>{cacheStore.GetString("lblFather")}: </strong>
                                            <span>{`${patient.FatherFirstName ?? ""} ${patient.FatherLastName ?? ""}`}</span>
                                        </div>
                                        <div className="flex-container flex-column address">
                                            {patient.Indicator01Name &&
                                                this.renderValue(patient.Indicator01Name, patient.Indicator01Value)
                                            }
                                            {patient.Indicator02Name &&
                                                this.renderValue(patient.Indicator02Name, patient.Indicator02Value)
                                            }
                                            {patient.HealthIdentifiers &&
                                                patient.HealthIdentifiers.map(healthIdentifier =>
                                                    this.renderValue(healthIdentifier.Name, healthIdentifier.Number, `helthIdentifier_${healthIdentifier.Number}`)
                                                )
                                            }
                                        </div>
                                    </div>
                                    <div>
                                        <div className="contact">
                                            <div className="flex-container phone">
                                                <div className="myle-icon-16 phone" />
                                                <div>
                                                    {patient.HomePhone && (
                                                        <div>
                                                            <span>{patient.HomePhone}</span>
                                                            <span className="lg">{` (${cacheStore.getLookupByContactPointType(ContactPointType.Personal).GetValue()})`}</span>
                                                        </div>
                                                    )}
                                                    {patient.WorkPhone && (
                                                        <div>
                                                            {!!patient.WorkPhoneExt &&
                                                                <span>{`${patient.WorkPhone} #${patient.WorkPhoneExt}`}</span>
                                                            }
                                                            {!patient.WorkPhoneExt &&
                                                                <span>{`${patient.WorkPhone}`}</span>
                                                            }
                                                            <span className="lg">{` (${cacheStore.getLookupByContactPointType(ContactPointType.Work).GetValue()})`}</span>
                                                        </div>
                                                    )}
                                                    {patient.CellPhone && (
                                                        <div>
                                                            <span>{`${patient.CellPhone}`}</span>
                                                            <span className="lg">{` (${cacheStore.GetString("CellPhoneLabel")})`}</span>
                                                        </div>
                                                    )}
                                                    {patient.OtherPhone && (
                                                        <div>
                                                            <div>
                                                                <span>{`${patient.OtherPhone}`}</span>
                                                                <span className="lg">{` (${cacheStore.GetString("OtherLabel")})`}</span>
                                                            </div>
                                                            <div className="padding-xs-left">{`${patient.OtherPhoneNote}`}</div>
                                                        </div>
                                                    )}
                                                    {patient.Email && (
                                                        <div>
                                                            <span>{`${patient.Email}`}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex-container address">
                                                <div className="myle-icon-16 pin" />
                                                {patient.StreetAddress && (
                                                    <div>
                                                        <div>
                                                            <span>{patient.StreetAddress} {patient.City}</span>
                                                        </div>
                                                        <div>
                                                            <span>{`${patient.PostalCode}, ${patient.Province}, ${patient.Country}`}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="lastupdatebox">
                                    {patient.LastUpdateDate &&
                                        <span>{`${cacheStore.GetString("LastUpdateLabel")} ${this.getFormattedDateString(patient.LastUpdateDate, "YYYY-MM-DD HH:mm:ss")}`}</span>
                                    }
                                </div>
                            </div>
                        );
                    })
                }
            </div>

        );
    }

    private renderValue(name: string, value: string, key: string = null): JSX.Element {
        return (
            <div key={key} className="flex-container" style={{ justifyContent: "flex-start", whiteSpace: "nowrap" }}>
                <div>
                    <strong>{`${name}: `}</strong>
                </div>
                <div style={{ marginLeft: "5px" }}>
                    <span>{value}</span>
                </div>
            </div>
        );
    }

    private mylePatientSearch(): JSX.Element {
        return (
            <div className="myle-view">
                <div className="myle-view-search">
                    <strong>{cacheStore.GetString("MyleFilesForLabel")}</strong>
                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                        {this.state.myleQueryText ?? " "}
                    </div>
                </div>
                <div className="external-search-results">
                    <div>
                        {this.state.mylePatients.length > 1 && (
                            <div className="ps-pad" style={{ marginTop: "5px" }}>
                                <strong>{cacheStore.GetString("ExternalSearchDuplicateMyleFileFoundLabel")} {cacheStore.GetString("ExternalSearchDuplicateContactSupportToMergeLabel")}</strong>
                            </div>
                        )}
                    </div>
                    <div className="limiter">
                        {this.state.isLoading && (
                            <img src={img_loading} style={{ marginTop: "27%", marginLeft: "46%" }} alt="loading" />
                        )}
                        {this.state.mylePatients.map(p =>
                            this.renderMylePatient(p)
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private renderMylePatient(patient: SearchPatientV2Result): JSX.Element {
        let gender = MyleHelper.genderLookupToEnum(patient.GenderLookupId);

        return (
            <div
                key={`mylepatient_${patient.ContactId}`}
                className="myle-m-patient"
                style={{ padding: "unset" }}
            >
                <div className="eternal-search-patient-card">
                    <div className="logo">
                        <img src={img_p_banner_icon_100} />
                    </div>
                    <div>
                        <div>
                            <a className="name">{patient.FirstName}, {patient.LastName}
                                {gender !== Gender.Unspecified && <span className={gender === Gender.Male ? "myle-icon-16 male" : "myle-icon-16 female"} />}
                            </a>
                        </div>
                        <div>
                            <strong>NAM: </strong>
                            <span><span>{patient.NAM}</span> {this.getNameExpiryFormatted(patient)}</span>
                        </div>
                        <div>
                            <strong>{cacheStore.GetString("DateOfBirthDLabel")} </strong>
                            <span>{this.getFormattedDateString(patient.DateOfBirth)}</span>
                        </div>
                        <div>
                            <strong>{cacheStore.GetString("lblMother")}: </strong>
                            <span>{`${patient.MotherFirstName ?? ""} ${patient.MotherLastName ?? ""}`}</span>
                        </div>
                        <div>
                            <strong>{cacheStore.GetString("lblFather")}: </strong>
                            <span>{`${patient.FatherFirstName ?? ""} ${patient.FatherLastName ?? ""}`}</span>
                        </div>
                    </div>
                    <div className="contact">

                        <div className="flex-container phone">
                            <div className="myle-icon-16 phone" />
                            {patient.PhoneNumbers.length > 0 && (
                                <div>
                                    {patient.PhoneNumbers.map((phone: PatientPhoneNumber, idx) => (
                                        <div key={`${idx}_${phone}`}>
                                            <span>{phone.toDisplayableString()}</span>
                                            <span className="lg">{` (${cacheStore.GetLocalizedValueByLookupId(phone.PhoneTypeLookupId)})`}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {patient.PhoneNumbers.length === 0 && <div><span>n/a</span></div>}
                        </div>

                        <div className="flex-container address">
                            <div className="myle-icon-16 pin" />
                            {patient.Addresses.length > 0 && (
                                <div>
                                    {patient.Addresses.map((a: PatientAddress, idx: number) => (
                                        <div key={idx + "__addrs"}>
                                            <div>
                                                <span>{a.Street} {a.City}</span>
                                            </div>
                                            <div><span>{cacheStore.GetLocalizedValueByLookupId(a.StateLookupId)} {a.ZipCode}</span>
                                                <span className="lg">
                                                    {` (${cacheStore.GetLocalizedValueByLookupId(a.ContactTypeLookupId)})`}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {patient.PhoneNumbers.length === 0 && <div><span>n/a</span></div>}
                        </div>
                    </div>
                </div>
                <div className="lastupdatebox">
                    {patient.LastUpdatedDate &&
                        <span>{`${cacheStore.GetString("LastUpdateLabel")} ${this.getFormattedDateString(patient.LastUpdatedDate, "YYYY-MM-DD HH:mm:ss")}`}</span>
                    }
                </div>
            </div>
        );
    }

    private getNameExpiryFormatted(patient: SearchPatientV2Result): string {
        const card = patient.HealthCards.firstOrDefault(c => c.Number === patient.NAM);
        return card
            ? this.getFormattedDateString("(YYYY/MM)")
            : "";
    }

    private getFormattedDateString(date: string, format: string = "YYYY/MM/DD"): string {
        if (IsNullOrWhiteSpace(date))
            return "";

        return moment(date).isValid()
            ? moment(date).format(format)
            : "";
    }

    private searchMylePatients(retries: SearchPatientWithDataRequest[] = []): void {
        const request = retries.shift();
        const initialPatientRefKey = this.state.selectedExternalPatientRefkey;

        this.mutateState(s => {
            s.mylePatients = [];
            s.isLoading = true;
            s.myleQueryText = request.ExternalSystemIds?.firstOrDefault()?.Number ?? request.NAM ?? request.CharNumber;
        }, async () => {
            const response = await Client.postAsync<any>(ServerOperations.Server.Myle_SearchPatientWithData, request);

            if (this.state.selectedExternalPatientRefkey != initialPatientRefKey) {
                this.mutateState(s => s.isLoading = false);
                return; // old search result
            }

            this.mutateState(
                s => {
                    s.mylePatients = response.data.Patients || [];

                    // init phone class for formatting
                    s.mylePatients.map(p => {
                        p.PhoneNumbers = p.PhoneNumbers?.map(phone => new PatientPhoneNumber(phone)) ?? [];
                        return p;
                    });

                    s.isLoading = false;
                    s.errorMessage = response.errors?.firstOrDefault()?.Message;
                },
                () => {
                    if (retries.any() && !response.data.Patients?.length)
                        this.searchMylePatients(retries);
                }
            );
        });
    }

    public onExternalSearchTextChange(newValue: string): void {
        this.debouncedExternalSearchTextChange(800);
        this.mutateState(s => s.externalQueryText = newValue); // typing
    }

    public onQueryKeyDown(e: React.KeyboardEvent): void {
        if (e.key === "Enter")
            this.searchExternalPatient(); // enter press
    }

    private debouncedExternalSearchTextChange(timeout: number = 250): void {
        clearTimeout(this.debounceExternalSearchRef);

        this.mutateState(
            s => {
                s.pendingExternalSearch = true;
                s.selectedExternalPatientRefkey = null;
                s.externalSearchPatients = {};
                s.mylePatients = [];
                s.myleQueryText = "";
                s.errorMessage = "";
            },
            () => {
                this.debounceExternalSearchRef = window.setTimeout(() => {
                    if (this.state.isSearchingExternal) {
                        this.debouncedExternalSearchTextChange();
                        return;
                    }
                    this.searchExternalPatient();
                }, timeout);
            }
        );
    }

    public searchExternalPatient(): void {
        if (IsNullOrWhiteSpace(this.state.externalQueryText)) {
            this.clearResults("");
            return;
        }

        const initialQueryText = this.state.externalQueryText;
        const initialExternalSystemCode = this.state.selectedExternalSearchSystemCode;

        this.mutateState(s => {
            s.isSearchingExternal = true;
            s.mylePatients = [];
            s.myleQueryText = "";
            s.errorMessage = "";
        }, async () => {
            const response = await Client.postAsync<ExternalSearchResponse>(
                ServerOperations.Server.Myle_ExternalSearch,
                {
                    QueryText: this.state.externalQueryText,
                    ExternalSearchSystemCode: this.state.selectedExternalSearchSystemCode
                }
            );

            if (initialQueryText != this.state.externalQueryText || initialExternalSystemCode != this.state.selectedExternalSearchSystemCode) {
                this.mutateState(
                    s => {
                        s.pendingExternalSearch = false;
                        s.isSearchingExternal = false;
                    }
                );
                return; // old result
            }

            if (response.errors?.length) {
                this.clearResults(response.errors[0].Message);
                return;
            }

            let externalResponses: ExternalSearchData[] = response.data.PatientData;
            this.mutateState(
                s => {
                    s.externalSearchPatients = externalResponses?.toDictionary(p => p.MRN) ?? {};
                    s.errorMessage = null;
                    s.pendingExternalSearch = false;
                    s.isSearchingExternal = false;
                    s.mylePatients = [];
                    s.myleQueryText = "";
                }
                , () => {
                    // select first patient automatically
                    if (externalResponses?.length == 1) {
                        this.onSelectExternalPatient(externalResponses[0].MRN);
                    }
                }
            );
        });
    }

    public clearResults(error: string = ""): void {
        this.mutateState(s => {
            s.mylePatients = [];
            s.myleQueryText = "";
            s.externalSearchPatients = null;
            s.errorMessage = error;
            s.pendingExternalSearch = false;
            s.isSearchingExternal = false;
        });
    }

    public onSelectExternalPatient(externalPatientRefKey: string): void {
        const selectedPatient = this.state.externalSearchPatients[externalPatientRefKey];
        if (!selectedPatient)
            return;

        this.mutateState(
            s => s.selectedExternalPatientRefkey = externalPatientRefKey,
            () => this.searchMyleFromExternalResponse(selectedPatient)
        );
    }

    public searchMyleFromExternalResponse(externalResponse: ExternalSearchData): void {
        let retries: SearchPatientWithDataRequest[] = [];

        if (this.props.patientToUpdateContactid) {
            // We known which myle patient to update.
            retries.push(new SearchPatientWithDataRequest({
                ShouldIncludeInactivePatients: true,
                PatientContactId: this.props.patientToUpdateContactid
            }));
        }
        else {
            // Search by Ids
            if (externalResponse.ExternalSystemIds?.any() == true) {
                retries.push(new SearchPatientWithDataRequest({
                    ShouldIncludeInactivePatients: true,
                    ExternalSystemIds: externalResponse.ExternalSystemIds
                }));
            }

            // Search by NAM
            if (!IsNullOrWhiteSpace(externalResponse.NAM)) {
                retries.push(new SearchPatientWithDataRequest({
                    NAM: externalResponse.NAM
                }));
            }

            // Search by MRN
            if (externalResponse.UseMRNForSearh && !IsNullOrWhiteSpace(externalResponse.MRN)) {
                retries.push(new SearchPatientWithDataRequest({
                    CharNumber: externalResponse.MRN
                }));
            }
        }

        if (retries.length == 0) {
            this.clearResults(cacheStore.GetString("ExternalSearchErrorMessage1Label") + " " + cacheStore.GetString("ExternalSearchErrorMessage2Label"));
            return;
        }

        this.searchMylePatients(retries);
    }

    public async importExternalPatient(isNAMValid: boolean = true): Promise<void> {
        const selectedPatient = this.state.externalSearchPatients?.[this.state.selectedExternalPatientRefkey] ?? null;
        if (selectedPatient == null)
            return;

        const requestData = {
            FirstName: selectedPatient.FirstName,
            LastName: selectedPatient.LastName,
            DateOfBirth: selectedPatient.BirthDate,
            GenderLookupId: cacheStore.GetGenderLookupId(selectedPatient.Sex),
            HealthCardRegionLookupId: cacheStore.FindLookupByLookupCode("CanadaState_QC").LookupID,
            NAM: selectedPatient.NAM,
            NAMExpiry: selectedPatient.NAMExp,
            MotherFirstName: selectedPatient.MotherFirstName,
            MotherLastName: selectedPatient.MotherLastName,
            FatherFirstName: selectedPatient.FatherFirstName,
            FatherLastName: selectedPatient.FatherLastName,

            CivicAddress: selectedPatient.StreetAddress,
            City: selectedPatient.City,
            PostalCode: selectedPatient.PostalCode,
            RegionLookupId: cacheStore.FindLookupByLookupCode("CanadaState_QC").LookupID,
            CountryLookupId: cacheStore.FindLookupByLookupCode("CNCANADA").LookupID,

            Phone1Number: selectedPatient.HomePhone,
            Phone1ContactTypeLookupId: cacheStore.FindLookupByLookupCode("PERSONAL").LookupID,
            Phone1TypeLookupId: cacheStore.FindLookupByLookupCode("PHONE").LookupID,
            Phone1Ext: null,
            Phone2Number: selectedPatient.WorkPhone,
            Phone2ContactTypeLookupId: cacheStore.FindLookupByLookupCode("WORK").LookupID,
            Phone2TypeLookupId: cacheStore.FindLookupByLookupCode("PHONE").LookupID,
            Phone2Ext: selectedPatient.WorkPhoneExt,
            Phone3Number: selectedPatient.CellPhone,
            Phone3ContactTypeLookupId: cacheStore.FindLookupByLookupCode("PERSONAL").LookupID,
            Phone3TypeLookupId: cacheStore.FindLookupByLookupCode("MOBILE").LookupID,
            OtherPhone: selectedPatient.OtherPhone,
            OtherPhoneNote: selectedPatient.OtherPhoneNote,

            EmailAddress: selectedPatient.Email,
            IsOkToContact: true,

            Identifier01: selectedPatient.Identifier01,
            Identifier02: selectedPatient.Identifier02,
            Identifier03: null,
            MyleChartNumber: selectedPatient.MyleChartNumber,
            HealthIdentifiers: selectedPatient.HealthIdentifiers,
            ExternalSystemIds: selectedPatient.ExternalSystemIds
        };

        if (!isNAMValid) {
            requestData.Identifier03 = selectedPatient.NAM;
            requestData.NAM = null;
        }

        const response = await Client.postAsync<any>(ServerOperations.Server.Myle_CreatePatient, requestData);

        if (response.errors?.length > 0) {
            this.clearResults(response.errors[0].Message);
            return;
        }
        this.props.onCancelCallback();
        patientStore.setActivePatientUI(response.data.PatientContactId);
    }

    public openPatient(patient: SearchPatientV2Result): void {
        this.props.onCancelCallback();
        patientStore.setActivePatientUI(patient.ContactId);
    }

    public async updatePatient(selectedMylePatient: SearchPatientV2Result): Promise<void> {
        const selectedPatient = this.state.externalSearchPatients?.[this.state.selectedExternalPatientRefkey] ?? null;
        if (selectedPatient == null)
            return;

        const response = await Client.postAsync<any>(
            ServerOperations.Server.Myle_ExternalSearch_UpdatePatient,
            {
                Patient: selectedMylePatient,
                FirstName: selectedPatient.FirstName,
                LastName: selectedPatient.LastName,
                DateOfBirth: selectedPatient.BirthDate,
                GenderId: cacheStore.GetGenderLookupId(selectedPatient.Sex),
                NAM: selectedPatient.NAM,
                NAMExp: selectedPatient.NAMExp,
                MotherLastName: selectedPatient.MotherLastName,
                MotherFirstName: selectedPatient.MotherFirstName,
                FatherFirstName: selectedPatient.FatherFirstName,
                FatherLastName: selectedPatient.FatherLastName,
                StreetAddress: selectedPatient.StreetAddress,
                City: selectedPatient.City,
                ProvinceId: cacheStore.FindLookupByLookupCode("CanadaState_QC").LookupID,
                CountryId: cacheStore.FindLookupByLookupCode("CNCANADA").LookupID,
                PostalCode: selectedPatient.PostalCode,
                HomePhone: selectedPatient.HomePhone,
                WorkPhone: selectedPatient.WorkPhone,
                WorkPhoneExt: selectedPatient.WorkPhoneExt,
                OtherPhone: selectedPatient.OtherPhone,
                OtherPhoneNote: selectedPatient.OtherPhoneNote,
                CellPhone: selectedPatient.CellPhone,
                Email: selectedPatient.Email,
                HealthIdentifiers: selectedPatient.HealthIdentifiers,
                ExternalSystemIds: selectedPatient.ExternalSystemIds
            }
        );

        if (response.errors?.length > 0) {
            this.clearResults(response.errors[0].Message);
            return;
        }

        patientStore.setActivePatientUI(this.state.mylePatients[0].ContactId);
        this.props.onSyncCallBack?.();
        this.props.onCancelCallback();
    }
}

class SearchPatientWithDataRequest {
    public ShouldIncludeInactivePatients: boolean = false;
    public NAM: string;
    public CharNumber: string;
    public PatientContactId: string;
    public ExternalSystemIds: ExternalSystemIds[];
    public MaxResultsToReturn: number = 20;

    constructor(init?: Partial<SearchPatientWithDataRequest>) {
        Object.assign(this, init);
    }
}

export { ExternalSearchComponent };
